
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useBlogPosts } from '@/hooks/useBlogPosts'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  PenTool, 
  FileText, 
  Eye, 
  Calendar, 
  Plus, 
  LogOut,
  Settings,
  BarChart3,
  List,
  Palette
} from 'lucide-react'

const AdminDashboardPage = () => {
  const { signOut, user } = useAuth()
  const { data: allPosts } = useBlogPosts()
  const { data: publishedPosts } = useBlogPosts('published')
  const { data: drafts } = useBlogPosts('draft')

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const stats = [
    {
      title: 'Articles publiés',
      value: publishedPosts?.length || 0,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Brouillons',
      value: drafts?.length || 0,
      icon: PenTool,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Total articles',
      value: allPosts?.length || 0,
      icon: BarChart3,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
            <p className="text-gray-600">Bienvenue, {user?.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Voir le site
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Actions rapides
              </CardTitle>
              <CardDescription>
                Gérez vos articles et votre contenu
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/admin/articles/new" className="block">
                <Button className="w-full justify-start bg-gradient-to-r from-pink-500 to-blue-500">
                  <PenTool className="w-4 h-4 mr-2" />
                  Nouvel article
                </Button>
              </Link>
              <Link to="/admin/articles" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <List className="w-4 h-4 mr-2" />
                  Gérer les articles
                </Button>
              </Link>
              <Link to="/admin/cms" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Palette className="w-4 h-4 mr-2" />
                  CMS - Gestionnaire de contenu
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Articles récents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allPosts?.slice(0, 3).map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {post.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(post.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                      {post.status === 'published' ? 'Publié' : 'Brouillon'}
                    </Badge>
                  </div>
                )) || (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Aucun article trouvé
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboardPage
